// src/pages/base/Base.jsx
// Refactored version with mission support

import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { THEMES, THEME_BUNDLES, getColorClasses } from '../themeConfig';

// Import modularized code
import { 
  useCampaignInitialization, 
  useDayAdvancement,
  useBaseAPI,
  useHealSoldier
} from './Base.hooks';
import { getDeepBackgroundImage } from './Base.constants';

// Import new sub-components
import BaseHeader from './components/BaseHeader';
import DaySummaryModal from './components/DaySummaryModal';

// Components
import BaseModules from '../../componets/BaseModules';
import SquadRoster from '../../componets/SquadRoster';
import ResearchLab from '../../componets/ResearchLab';
import { MissionControl } from '../../componets/missioncontrol';
import Medbay from '../../componets/MedBay';

/**
 * Calculate power statistics from facilities
 */
const calculatePowerStats = (facilities) => {
  const generatorCount = facilities.filter(
    f => f.facility_type === 'generator' && (f.is_active === true || f.is_active === undefined)
  ).length;
  
  const facilityPowerCosts = {
    barracks: 10,
    medbay: 20,
    research: 25,
    workshop: 15,
    command: 5
  };

  const powerUsed = facilities
    .filter(f => f.facility_type !== 'generator' && (f.is_active === true || f.is_active === undefined))
    .reduce((total, facility) => total + (facilityPowerCosts[facility.facility_type] || 0), 0);
  
  const powerCapacity = generatorCount * 100;
  
  return { powerUsed, powerCapacity };
};

const ThemedBase = ({ theme: propTheme = 'vanilla' }) => {
  const location = useLocation();
  const [selectedTab, setSelectedTab] = useState('base');
  const [currentCampaign, setCurrentCampaign] = useState(null);
  const [currentResearch, setCurrentResearch] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [squad, setSquad] = useState([]);
  const [research, setResearch] = useState([]);
  const [activeMissions, setActiveMissions] = useState([]); // ← NEW: Track active missions
  const [resources, setResources] = useState({
    funds: 0,
    supplies: 0,
    intel: 0,
    power: 0,
    powerUsed: 0,
    powerCapacity: 0
  });
  const [dayEvents, setDayEvents] = useState([]);
  const [showDaySummary, setShowDaySummary] = useState(false);

  // Use campaign theme if available, otherwise fall back to prop theme
  const theme = currentCampaign?.theme || propTheme;
  const config = THEMES[theme] || THEMES.vanilla;
  const themeBundle = THEME_BUNDLES[theme] || THEME_BUNDLES.vanilla;

  // All state setters grouped for hooks
  const setters = {
    setCurrentCampaign,
    setResources,
    setFacilities,
    setResearch,
    setCurrentResearch,
    setSquad,
    setActiveMissions, // ← NEW: Add to setters
    setDayEvents,
    setShowDaySummary
  };

  // Custom hooks handle all the complex logic
  const { loading } = useCampaignInitialization(location, setters);
  
  // Day advancement hook - now includes mission progression
  const { handleAdvanceDay, isAdvancingDay, dayProgress } = useDayAdvancement(
    currentCampaign,
    { squad, facilities, currentResearch, research, resources, activeMissions }, // ← Pass activeMissions
    setters,
    themeBundle
  );

  const apiHandlers = useBaseAPI(currentCampaign, { setSquad, setResearch, setFacilities });
  
  const handleHealSoldier = useHealSoldier(apiHandlers, setResources);

  // ========== MISSION HANDLERS ==========
  
  const handleDeploySquad = (payload) => {
    console.log('🚀 Deploying squad on mission:', payload);
    
    // Update squad members to "On Mission" status
    setSquad(prevSquad => 
      prevSquad.map(soldier => {
        const isDeployed = payload.squad.find(s => s.id === soldier.id);
        return isDeployed ? { ...soldier, status: 'On Mission' } : soldier;
      })
    );
    
    // Add mission to active missions
    setActiveMissions(prev => [...prev, payload.mission]);
    
    console.log('✅ Mission deployed:', payload.mission.name);
  };

  // Sync power stats with resources when facilities change
  useEffect(() => {
    if (facilities.length > 0) {
      const { powerUsed, powerCapacity } = calculatePowerStats(facilities);
      setResources(prev => ({
        ...prev,
        powerUsed,
        powerCapacity
      }));
    }
  }, [facilities]);

  // ========== RESEARCH HANDLERS ==========
  
  const handleStartResearch = async (project) => {
    // Validate resources before starting
    if (resources.funds < project.cost.funds ||
        resources.supplies < project.cost.supplies ||
        resources.intel < project.cost.intel) {
      console.error('❌ Insufficient resources to start research');
      return;
    }

    // Check if research is already in progress
    if (currentResearch) {
      console.error('❌ Research already in progress');
      return;
    }

    // Deduct resources immediately (optimistic update)
    setResources(prev => ({
      ...prev,
      funds: prev.funds - project.cost.funds,
      supplies: prev.supplies - project.cost.supplies,
      intel: prev.intel - project.cost.intel
    }));
    
    try {
      // ✅ CREATE DATABASE RECORD
      const createdResearch = await apiHandlers.startResearch(
        currentCampaign.id, 
        project
      );
      
      // ✅ Set as current research with DATABASE ID
      const newResearch = {
        id: createdResearch.id,
        campaign_id: currentCampaign.id,
        research_type: project.id,
        research_name: project.name,
        progress: 0,
        completed: 0,
        started_at: createdResearch.started_at,
        duration: project.duration,
        cost: project.cost,
        benefits: project.benefits,
        category: project.category
      };
      
      setCurrentResearch(newResearch);
      
      console.log('✅ Research started:', {
        name: newResearch.research_name,
        id: newResearch.id,
        type: newResearch.research_type
      });
      
    } catch (error) {
      console.error('❌ Error starting research:', error);
      
      // Restore resources on error (rollback)
      setResources(prev => ({
        ...prev,
        funds: prev.funds + project.cost.funds,
        supplies: prev.supplies + project.cost.supplies,
        intel: prev.intel + project.cost.intel
      }));
      
      alert('Failed to start research. Please try again.');
    }
  };

  // Get completed research IDs for ResearchLab
  const completedResearchIds = research
    .filter(r => r.completed === 1)
    .map(r => r.research_type);

  const deepBackgroundImage = getDeepBackgroundImage(theme);

  // Loading state
  if (loading) {
    return (
      <div className={`min-h-screen bg-gradient-to-b ${config.colors.background} text-white flex items-center justify-center`}>
        <div className="text-center">
          <div className={`animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 ${getColorClasses(config.colors.primary, 'border')} mx-auto mb-4`}></div>
          <p className={`text-xl font-bold ${getColorClasses(config.colors.primary)} tracking-wider`}>
            {config.loading || 'LOADING BASE...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gradient-to-b ${config.colors.background} text-white`}>
      {/* Deep background layer */}
      <div 
        className="fixed inset-0 opacity-40 bg-cover bg-center pointer-events-none"
        style={{ backgroundImage: `url(${deepBackgroundImage})` }}
      />
      
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(#333 1px, transparent 1px), linear-gradient(90deg, #333 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }} />
      </div>

      {/* Header with resources and tabs */}
      <BaseHeader
        config={config}
        currentCampaign={currentCampaign}
        resources={resources}
        handleAdvanceDay={handleAdvanceDay}
        isAdvancingDay={isAdvancingDay}
        dayProgress={dayProgress}
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
      />

      {/* Main content area */}
      <div className="relative max-w-7xl mx-auto px-4 py-8">
        {selectedTab === 'base' && (
          <BaseModules 
            resources={resources} 
            setResources={setResources}
            facilities={facilities}
            currentCampaign={currentCampaign}
            apiHandlers={apiHandlers}
            theme={theme}
          />
        )}

        {selectedTab === 'squad' && (
          <SquadRoster 
            squad={squad}
            resources={resources}
            setResources={setResources}
            currentCampaign={currentCampaign}
            apiHandlers={apiHandlers}
            theme={theme}
          />
        )}

        {selectedTab === 'medbay' && (
          <Medbay 
            squad={squad}
            resources={resources}
            onHealSoldier={handleHealSoldier}
            theme={theme}
          />
        )}

        {selectedTab === 'research' && (
          <ResearchLab 
            resources={resources}
            onStartResearch={handleStartResearch}
            currentResearch={currentResearch}
            completedResearch={completedResearchIds}
            theme={theme}
          />
        )}

        {selectedTab === 'missions' && (
          <MissionControl 
            squad={squad}
            resources={resources}
            onDeploySquad={handleDeploySquad}
            activeMissions={activeMissions}
            threatLevel={currentCampaign?.threat_level || 3}
            currentCampaign={currentCampaign}
            theme={theme}
          />
        )}
      </div>

      {/* Day Summary Modal */}
      {currentCampaign && (
        <DaySummaryModal
          show={showDaySummary}
          onClose={() => setShowDaySummary(false)}
          currentCampaign={currentCampaign}
          facilities={facilities}
          currentResearch={currentResearch}
          squad={squad}
          dayEvents={dayEvents}
          config={config}
        />
      )}
    </div>
  );
};

export const VanillaBase = () => <ThemedBase theme="vanilla" />;
export const MajesticBase = () => <ThemedBase theme="majestic" />;
export const DevastatorsBase = () => <ThemedBase theme="devastators" />;
export const ArchboundBase = () => <ThemedBase theme="archbound" />;

export default ThemedBase;